import React, { isValidElement, useEffect, useState } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import { useRouter } from 'next/router'
import { AiFillCloseCircle } from 'react-icons/ai'
import InputBase from '@mui/material/InputBase'
import IconButton from '@mui/material/IconButton'

import { Place } from 'components/icons'
import { PRIMARY } from 'constants/colors'
import { OptionSet } from 'components/controls/options'
import { useMapGeocoder } from '../MapGeocoder/useMapGeocoder'
import * as Async from 'contexts/async'
import * as MapContext from 'contexts/map'
import css from '../MapGeocoder/MapGeocoder.module.css'

export const Geocoder = () => {
    const router = useRouter()
    const { query } = router
    const intl = useIntl()
    const map = MapContext.useMap()
    const {
        term,
        hasTerm,
        terms,
        active,
        activate,
        deactivate,
        selectedIndex,
        setTerm,
        setTermCount,
        onFocus,
        onKeyDown,
    } = useMapGeocoder()

    const placeholder = intl.formatMessage({
        description: 'Geocoder',
        defaultMessage: 'Search Places…',
    })

    const handleChange = event => {
        const { value } = event.target
        // check if first character is a number
        if (value[0] && isNaN(value[0])) {
            // if not a number, activate the geocoder
            activate()
        }
        setTerm(value)
    }

    const clearTerm = () => {
        setTerm('')
        router.push(
            {
                query: {
                    date: query.date,
                },
            },
            undefined,
            { shallow: true }
        )
    }

    const focusOnPoint = () => {
        const { lat, lng } = query
        map &&
            map.flyTo({
                center: [lng, lat],
                zoom: 8,
            })
    }

    const handleOptionClick = place => {
        const { latitude, longitude } = place
        deactivate()
        setTerm(place.name)
        router.push(
            {
                query: {
                    ...query,
                    lat: latitude,
                    lng: longitude,
                    place: place.name,
                },
            },
            undefined,
            { shallow: true }
        )
    }

    const updateCoordinates = (lat, lng) => {
        map &&
            map.flyTo({
                center: [lng, lat],
                zoom: 8,
            })
    }

    const setValuesFromQuery = () => {
        const { lat, lng, place } = query

        if (place && lat && lng) {
            setTerm(place)
            updateCoordinates(lat, lng)
        } else if (lat && lng) {
            setTerm(`${lat}, ${lng}`)
            updateCoordinates(lat, lng)
        }
    }

    useEffect(() => {
        setValuesFromQuery()
    }, [query])

    // useEffect(() => {
    //     // on page load, if there are coordinates in the query, fly to them
    //     // TODO:: currently the map zooms to these points then zooms out to the default zoom level
    //     // should not zoom out to default zoom level
    //     setCoordinatesFromQuery()
    // }, [map])

    return (
        <>
            {active && <div style={styles.mask} />}
            <div style={styles.inputContainer}>
                <IconButton onClick={focusOnPoint}>
                    <Place color={PRIMARY} />
                </IconButton>
                <InputBase
                    sx={{ ml: 1, flex: 1 }}
                    placeholder={placeholder}
                    onChange={handleChange}
                    onFocus={onFocus}
                    onBlur={deactivate}
                    value={term}
                    onKeyDown={event => onKeyDown(event, handleOptionClick)}
                />
                {term.length ? (
                    <IconButton
                        onClick={clearTerm}
                        type="button"
                        sx={{ p: '10px' }}
                        aria-label="search">
                        <AiFillCloseCircle size="16px" style={styles.clearIcon} />
                    </IconButton>
                ) : (
                    <> </>
                )}
            </div>
            <Async.Provider value={terms}>
                <Async.Found>
                    {terms => {
                        if (!hasTerm || !active) {
                            return null
                        }

                        if (terms.length === 0) {
                            return (
                                <div>
                                    <FormattedMessage
                                        defaultMessage="No results found"
                                        description="Map geocoder search results"
                                    />
                                </div>
                            )
                        }

                        setTermCount(terms.length)

                        return (
                            <div style={styles.OptionSet}>
                                <OptionSet onChange={handleOptionClick}>
                                    {terms.map((place, index) => {
                                        const selected = index === selectedIndex

                                        return (
                                            <Option
                                                key={place.id}
                                                value={place}
                                                selected={selected}>
                                                {place.name}
                                            </Option>
                                        )
                                    })}
                                </OptionSet>
                            </div>
                        )
                    }}
                </Async.Found>
            </Async.Provider>
        </>
    )
}

const Option = ({ children, value, selected, onClick = noop }) => {
    const title = isValidElement(children) ? value : children
    const name = selected ? 'archiveOption--Selected' : 'archiveOption'

    const handleMouseDown = () => {
        onClick(value)
    }

    return (
        <div className={css[name]} title={title} onMouseDown={handleMouseDown}>
            {children}
        </div>
    )
}

const styles = {
    OptionSet: {
        position: 'absolute',
        backgroundColor: '#fff',
        width: '90%',
        zIndex: 4,
    },
    option: {
        padding: '12px 10px',
        borderBottom: '1px solid #ccc',
        width: '100%',
        cursor: 'pointer',
        zIndex: 5,
    },
    clearIcon: {
        color: '#ccc',
    },
    selectedOption: {
        padding: '12px 10px',
        borderBottom: '1px solid #ccc',
        cursor: 'pointer',
        backgroundColor: '#eee',
        zIndex: 5,
    },
    inputContainer: {
        width: '100%',
        height: 'fit-content',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #ccc',
    },
    geoInput: {
        width: '100%',
        zIndex: 2,
    },
    mask: {
        position: 'absolute',
        top: 150,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
    },
}
